import { useState, useEffect, useRef, useMemo } from 'react';
import Api from 'entities/helpers/api';
import cadenceGqlCall from '../utils/cadence-gql-call';
import { CADENCE_MEMBERS_QUERY } from '../utils/cadence-queries';

/** Lightweight bulk query — just the names needed to search the full member list. */
const CONTACT_NAMES_QUERY = `query($ids: [BigInt!]) {
  contacts(ids: $ids) {
    id
    contact_attributes {
      name_full
    }
  }
}`;

const CONTACT_DETAIL_QUERY = `query($id: BigInt!) {
  contact(id: $id) {
    id
    contact_attributes {
      name_full
      name_first
      name_last
    }
    emails {
      email
      primary
      type
    }
    phones {
      phone
      primary
      type
    }
    profile_image {
      url
    }
  }
}`;

/** Fetch contact details using the same API pattern as profile-header.js. */
const fetchContactDetails = (contactId) => {
  const numericId = Number(contactId);
  if (!numericId) return Promise.resolve(null);

  return Api.GRAPHQL.GRAPHQL.post({
    data: JSON.stringify({
      operationName: null,
      query: CONTACT_DETAIL_QUERY,
      variables: { id: numericId },
    }),
  }).then((resp) => {
    const contact = resp?.data?.contact || resp?.contact;
    if (!contact) return null;

    const primaryEmail = (contact.emails || []).find((e) => e.primary)?.email || contact.emails?.[0]?.email || null;
    const primaryPhone = (contact.phones || []).find((p) => p.primary)?.phone || contact.phones?.[0]?.phone || null;

    return {
      contactName: contact.contact_attributes?.name_full || null,
      contactFirstName: contact.contact_attributes?.name_first || null,
      contactLastName: contact.contact_attributes?.name_last || null,
      contactEmail: primaryEmail,
      contactPhone: primaryPhone,
      contactImage: contact.profile_image?.url || null,
    };
  });
};

/** Number of contact IDs to request per bulk name lookup. */
const CONTACT_IDS_PER_REQUEST = 200;

/**
 * Bulk-fetch full names for a list of contact IDs, returning a
 * `{ [contactId]: name_full }` index. Used to make recipient search span the
 * full member list (not just the enriched current page) without fetching every
 * contact's full detail. Requests are chunked so a large recipient list does
 * not produce one oversized query.
 */
const fetchContactNames = async (contactIds) => {
  const ids = [...new Set(contactIds.map(Number).filter(Boolean))];
  if (ids.length === 0) return {};

  const chunks = [];
  for (let i = 0; i < ids.length; i += CONTACT_IDS_PER_REQUEST) {
    chunks.push(ids.slice(i, i + CONTACT_IDS_PER_REQUEST));
  }

  const contactGroups = await batchProcess(
    chunks,
    async (chunk) => {
      const resp = await Api.GRAPHQL.GRAPHQL.post({
        data: JSON.stringify({ operationName: null, query: CONTACT_NAMES_QUERY, variables: { ids: chunk } }),
      });
      return resp?.data?.contacts || resp?.contacts || [];
    },
    3
  );

  const index = {};
  for (const contact of contactGroups.flat()) {
    if (contact?.id != null) index[String(contact.id)] = contact.contact_attributes?.name_full || '';
  }
  return index;
};

/**
 * Map failureEvents from the GraphQL response to the issues array format
 * expected by the RecipientDetailPanel.
 */
export const mapFailureEventsToIssues = (failureEvents) => {
  if (!failureEvents || failureEvents.length === 0) return [];
  return failureEvents.map((fe) => ({
    type: fe.failureType === 'missing_merge_field' ? 'warning' : 'error',
    message: fe.failureReason || fe.failureType || 'Unknown issue',
    code: fe.failureType || 'unknown',
    step: fe.cadenceEventId ? `Event ${fe.cadenceEventId}` : null,
  }));
};

/**
 * Process an array in batches of `size`, awaiting each batch before starting the next.
 * Prevents overwhelming the API with N concurrent requests.
 */
export const batchProcess = async (items, fn, size = 5) => {
  const results = [];
  for (let i = 0; i < items.length; i += size) {
    const batch = items.slice(i, i + size);
    const batchResults = await Promise.all(batch.map(fn));
    results.push(...batchResults);
  }
  return results;
};

/**
 * Fetch members for a cadence with paginated detail loading.
 *
 * Fetches all member data in one cadenceMembers connection query, then
 * enriches only the current page with contact details (name, email, phone, image).
 *
 * Search (when `searchQuery` is provided) spans the full member list rather
 * than only the enriched current page: a name index is bulk-fetched for all
 * members, then matched against name, email, and contact ID.
 *
 * @param {string|number} cadenceId
 * @param {Object} options
 * @param {number} options.page - 0-indexed page number
 * @param {number} options.pageSize - Number of members per page
 * @param {Object} options.filter - Optional filter input
 * @param {string} options.searchQuery - Optional free-text search across all members
 */
const useCadenceMembers = (cadenceId, { page = 0, pageSize = 10, filter = null, searchQuery = '' } = {}) => {
  const [allMembers, setAllMembers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPage, setLoadingPage] = useState(false);
  const [error, setError] = useState(null);
  const [fetchKey, setFetchKey] = useState(0);
  const enrichmentCacheRef = useRef({});
  // Name index for the full member list (contactId -> name_full), built lazily
  // the first time a search runs against the current allMembers set. `null`
  // means "not yet built"; the ref guards against duplicate in-flight builds.
  const [nameIndex, setNameIndex] = useState(null);
  const nameIndexLoadingRef = useRef(false);

  // Fetch all members from the connection (single query, full node data)
  useEffect(() => {
    if (!cadenceId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setMembers([]);
    setTotalCount(0);
    enrichmentCacheRef.current = {};
    setNameIndex(null);
    nameIndexLoadingRef.current = false;

    const fetchAll = async () => {
      const variables = { cadenceId: String(cadenceId) };
      if (filter) variables.filter = filter;
      const data = await cadenceGqlCall(CADENCE_MEMBERS_QUERY, variables);

      if (cancelled) return;

      const count = data.cadenceMembers?.totalCount || 0;
      setTotalCount(count);

      const nodes = (data.cadenceMembers?.edges || []).map((e) => e.node).filter(Boolean);

      setAllMembers(nodes);
    };

    fetchAll()
      .catch((err) => {
        console.warn('[cadence-members] fetchAll error:', err);
        if (!cancelled) setError(err.message || 'Failed to fetch members');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [cadenceId, fetchKey, JSON.stringify(filter)]); // eslint-disable-line react-hooks/exhaustive-deps

  const isSearching = !!searchQuery.trim();

  // Build the full-list name index the first time a search runs. Names aren't
  // present on the member nodes (only contactId/email), so without this a
  // search would only match the enriched current page.
  useEffect(() => {
    if (!isSearching || allMembers.length === 0 || nameIndex !== null || nameIndexLoadingRef.current) return;

    nameIndexLoadingRef.current = true;
    let cancelled = false;

    fetchContactNames(allMembers.map((m) => m.contactId))
      .then((index) => {
        if (!cancelled) setNameIndex(index);
      })
      .catch((err) => {
        // Degrade gracefully to email/contactId search rather than retrying forever.
        console.warn('[cadence-members] name index fetch failed:', err);
        if (!cancelled) setNameIndex({});
      })
      .finally(() => {
        nameIndexLoadingRef.current = false;
      });

    return () => {
      cancelled = true;
    };
  }, [isSearching, allMembers, nameIndex]);

  // The members matching the active search, across the full list. `null` means
  // a search is active but the name index is still loading.
  const matchedMembers = useMemo(() => {
    if (!isSearching) return allMembers;
    if (nameIndex === null) return null;
    const q = searchQuery.trim().toLowerCase();
    return allMembers.filter((m) => {
      const name = (nameIndex[String(m.contactId)] || '').toLowerCase();
      return name.includes(q) || (m.email || '').toLowerCase().includes(q) || String(m.contactId || '').includes(q);
    });
  }, [isSearching, allMembers, nameIndex, searchQuery]);

  const searchLoading = isSearching && matchedMembers === null && allMembers.length > 0;

  // Enrich the current page with contact details
  useEffect(() => {
    if (matchedMembers === null) return; // waiting on the name index
    if (matchedMembers.length === 0) {
      setMembers([]);
      setLoadingPage(false);
      return;
    }

    let cancelled = false;

    const start = page * pageSize;
    const pageMembers = matchedMembers.slice(start, start + pageSize);
    const uncached = pageMembers.filter((m) => !enrichmentCacheRef.current[String(m.id)]);

    // Already enriched (cached) — update synchronously so paging/search keystrokes
    // don't flash the loading spinner.
    if (uncached.length === 0) {
      setMembers(pageMembers.map((m) => enrichmentCacheRef.current[String(m.id)] || m));
      return;
    }

    setLoadingPage(true);

    const enrichPage = async () => {
      try {
        const enriched = await batchProcess(
          uncached,
          async (m) => {
            const issues = mapFailureEventsToIssues(m.failureEvents);
            if (!m.contactId) return { ...m, contactName: 'Unknown', contactEmail: m.email || null, issues };
            try {
              const details = await fetchContactDetails(m.contactId);
              return {
                ...m,
                contactName: details?.contactName || `Contact ${m.contactId}`,
                contactFirstName: details?.contactFirstName || null,
                contactLastName: details?.contactLastName || null,
                contactEmail: m.email || details?.contactEmail || null,
                contactPhone: details?.contactPhone || null,
                contactImage: details?.contactImage || null,
                issues,
              };
            } catch (err) {
              console.warn('[cadence-members] contact lookup failed for', m.contactId, err);
              return { ...m, contactName: `Contact ${m.contactId}`, issues };
            }
          },
          5
        );

        if (cancelled) return;

        for (const m of enriched) {
          enrichmentCacheRef.current[String(m.id)] = m;
        }

        const result = pageMembers.map((m) => enrichmentCacheRef.current[String(m.id)] || m);
        setMembers(result);
        setError(null);
      } catch (err) {
        console.warn('[cadence-members] enrichPage error:', err);
        if (!cancelled) setError(err.message || 'Failed to fetch page');
      } finally {
        if (!cancelled) setLoadingPage(false);
      }
    };

    enrichPage();

    return () => {
      cancelled = true;
    };
  }, [matchedMembers, page, pageSize]);

  const refetch = () => {
    enrichmentCacheRef.current = {};
    setFetchKey((k) => k + 1);
  };

  return {
    members,
    totalCount: isSearching && matchedMembers ? matchedMembers.length : totalCount,
    loading: loading || loadingPage || searchLoading,
    error,
    refetch,
  };
};

export default useCadenceMembers;
